import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button, Slider, cn } from '@llura/ui';
import { AnalyticsEvents, AnalyticsProps } from '@llura/constants';
import type {
  BigFiveAnswer,
  BigFiveAnswerValue,
  BigFiveQuestionId,
} from '@llura/types';

import { GlowLogo } from '../../shared/glow-logo';
import { useCoachStore } from '../../stores/coach.store';
import { useIntroStore } from '../../stores/intro.store';
import { usePersonalityStore } from '../../stores/personality.store';
import { useHaptic } from '../../hooks/useHaptic';
import { useStrings } from '../../hooks/useStrings';
import { useScrollToTop } from '../../hooks/use-scroll-to-top';
import { trackEvent } from '../../lib/analytics';
import { BIG_FIVE_QUESTIONS, BIG_FIVE_TOTAL } from './questions';
import { scoreAnswers, fallbackNarrative } from './scoring';

type Phase = 'intro' | 'question' | 'submitting' | 'thanks';

const DEFAULT_LIKERT: BigFiveAnswerValue = 3;

/**
 * Big Five test — sits between intro completion and coach onboarding.
 *
 * Phases:
 *   intro       — story-screen pitch + Begin / Skip
 *   question(i) — one Likert (1..5) per screen, with a "skip the rest" link
 *   submitting  — calls the API; on success persists scores+narrative, navigates
 *
 * Skipping at any phase POSTs /me/personality/skip (idempotent), sets
 * `seen=true` on the personality store, and goes to /onboarding/coach.
 */
export function PersonalityTestPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings, locale, interp } = useStrings();
  const introCompleted = useIntroStore((s) => s.completed);
  const isConfigured = useCoachStore((s) => s.isConfigured);
  const setResult = usePersonalityStore((s) => s.setResult);
  const setSkipped = usePersonalityStore((s) => s.setSkipped);

  const startedAtRef = useRef<number>(performance.now());
  const startedRef = useRef(false);
  // Snapshot `seen` at mount, not the live store value. Otherwise the success
  // branch of `submit()` (which flips seen → true via `setResult`) would
  // re-render and bounce us away before the thanks phase can paint.
  const seenAtMountRef = useRef(usePersonalityStore.getState().seen);

  const [phase, setPhase] = useState<Phase>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<BigFiveQuestionId, BigFiveAnswerValue>>(
    () =>
      BIG_FIVE_QUESTIONS.reduce(
        (acc, q) => ({ ...acc, [q.id]: DEFAULT_LIKERT }),
        {} as Record<BigFiveQuestionId, BigFiveAnswerValue>,
      ),
  );

  useScrollToTop(`${phase}-${questionIndex}`);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startedAtRef.current = performance.now();
    trackEvent(AnalyticsEvents.PersonalityTestStarted, {});
  }, []);

  // Gate: never serve the test outside its slot in the flow. Use the
  // mount-time snapshot so finishing the test (which flips seen → true)
  // doesn't bounce us before the thanks screen paints.
  if (!introCompleted) return <Navigate to="/intro" replace />;
  if (seenAtMountRef.current) {
    return <Navigate to={isConfigured ? '/dashboard' : '/onboarding/coach'} replace />;
  }

  const t = strings.personalityTest;

  const skip = (source: 'intro' | 'mid') => {
    haptic.tap();
    trackEvent(AnalyticsEvents.PersonalityTestSkipped, {
      [AnalyticsProps.PersonalityTestOutcome]:
        source === 'intro' ? 'skipped_intro' : 'skipped_mid',
      [AnalyticsProps.PersonalityTestAnswered]: source === 'intro' ? 0 : questionIndex,
      [AnalyticsProps.DurationMs]: Math.round(performance.now() - startedAtRef.current),
    });
    setSkipped();
    navigate('/onboarding/coach', { replace: true });
  };

  const begin = () => {
    haptic.tap();
    setPhase('question');
    setQuestionIndex(0);
  };

  const back = () => {
    haptic.tap();
    if (phase !== 'question') return;
    if (questionIndex === 0) {
      setPhase('intro');
      return;
    }
    setQuestionIndex((i) => i - 1);
  };

  const advance = () => {
    haptic.tap();
    if (questionIndex < BIG_FIVE_TOTAL - 1) {
      setQuestionIndex((i) => i + 1);
      return;
    }
    void submit();
  };

  const submit = async () => {
    setPhase('submitting');
    const payload: BigFiveAnswer[] = BIG_FIVE_QUESTIONS.map((q) => ({
      questionId: q.id,
      value: answers[q.id],
    }));
    // TODO(auth): route this back through api.me.submitPersonality(...) once
    // /me/* endpoints are guarded by a real session. Until then we score
    // locally so the test completes without hitting the 401 wall.
    const scores = scoreAnswers(payload);
    const narrative = fallbackNarrative(scores, locale);
    // Keep the 'submitting' spinner on for a beat so the transition reads as
    // a real "we're processing your answers" moment rather than a flicker.
    await new Promise((r) => setTimeout(r, 800));
    setResult({ scores, narrative, narrativeLocale: locale, rawAnswers: payload });
    trackEvent(AnalyticsEvents.PersonalityTestCompleted, {
      [AnalyticsProps.PersonalityTestOutcome]: 'completed',
      [AnalyticsProps.PersonalityTestAnswered]: BIG_FIVE_TOTAL,
      [AnalyticsProps.DurationMs]: Math.round(performance.now() - startedAtRef.current),
      [AnalyticsProps.PersonalityScoreOpenness]: scores.openness,
      [AnalyticsProps.PersonalityScoreConscientiousness]: scores.conscientiousness,
      [AnalyticsProps.PersonalityScoreExtraversion]: scores.extraversion,
      [AnalyticsProps.PersonalityScoreAgreeableness]: scores.agreeableness,
      [AnalyticsProps.PersonalityScoreNeuroticism]: scores.neuroticism,
    });
    haptic.success();
    setPhase('thanks');
  };

  const finishToCoach = () => {
    haptic.tap();
    navigate('/onboarding/coach', { replace: true });
  };

  const onValueChange = (id: BigFiveQuestionId) => (next: number[]) => {
    const v = clampLikert(next[0]);
    setAnswers((prev) => ({ ...prev, [id]: v }));
  };

  return (
    <div className="relative flex min-h-[100svh] flex-col overflow-hidden bg-background px-5">
      <BackgroundBlooms />

      <AnimatePresence mode="wait">
        {phase === 'intro' && (
          <IntroPhase
            key="intro"
            tag={t.intro.tag}
            title={t.intro.title}
            italic={t.intro.italic}
            body={t.intro.body}
            beginCta={t.intro.beginCta}
            skipCta={t.intro.skipCta}
            onBegin={begin}
            onSkip={() => skip('intro')}
            discretion={strings.intro.discretionFooter}
            backAria={t.backAria}
            onBack={() => navigate('/intro')}
          />
        )}
        {phase === 'question' && (
          <QuestionPhase
            key={`q-${questionIndex}`}
            index={questionIndex}
            total={BIG_FIVE_TOTAL}
            questionId={BIG_FIVE_QUESTIONS[questionIndex].id}
            text={t.questions[BIG_FIVE_QUESTIONS[questionIndex].id]}
            value={answers[BIG_FIVE_QUESTIONS[questionIndex].id]}
            onChange={onValueChange(BIG_FIVE_QUESTIONS[questionIndex].id)}
            scaleLabels={t.scaleLabels}
            progressLabel={interp(t.progress, {
              current: questionIndex + 1,
              total: BIG_FIVE_TOTAL,
            })}
            nextCta={t.nextCta}
            isLast={questionIndex === BIG_FIVE_TOTAL - 1}
            backAria={t.backAria}
            onBack={back}
            onNext={advance}
            skipCta={t.skipMidQuiz}
            onSkip={() => skip('mid')}
            discretion={strings.intro.discretionFooter}
          />
        )}
        {phase === 'submitting' && (
          <SubmittingPhase
            key="submitting"
            title={t.submitting.title}
            italic={t.submitting.italic}
            body={t.submitting.body}
            locale={locale}
          />
        )}
        {phase === 'thanks' && (
          <ThanksPhase
            key="thanks"
            title={t.thanks.title}
            italic={t.thanks.italic}
            body={t.thanks.body}
            ctaToCoach={t.thanks.ctaToCoach}
            onContinue={finishToCoach}
            discretion={strings.intro.discretionFooter}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function clampLikert(n: number | undefined): BigFiveAnswerValue {
  if (n === undefined || Number.isNaN(n)) return DEFAULT_LIKERT;
  const r = Math.round(n);
  if (r < 1) return 1;
  if (r > 5) return 5;
  return r as BigFiveAnswerValue;
}

function BackgroundBlooms() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary/12 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-secondary/12 blur-3xl"
      />
    </>
  );
}

function IntroPhase({
  tag,
  title,
  italic,
  body,
  beginCta,
  skipCta,
  onBegin,
  onSkip,
  discretion,
  backAria,
  onBack,
}: {
  tag: string;
  title: string;
  italic: string;
  body: string;
  beginCta: string;
  skipCta: string;
  onBegin: () => void;
  onSkip: () => void;
  discretion: string;
  backAria: string;
  onBack: () => void;
}) {
  return (
    <motion.div
      className="relative z-10 flex flex-1 flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      <header className="flex items-center pt-5">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label={backAria}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-5 py-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-accent">{tag}</p>
        <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground sm:text-5xl">
          {title}
        </h1>
        <p className="font-serif italic text-lg leading-relaxed text-muted-foreground sm:text-xl">
          {italic}
        </p>
        <p className="max-w-md text-balance text-sm leading-relaxed text-foreground/85 sm:text-base">
          {body}
        </p>
      </main>

      <footer className="mx-auto w-full max-w-md space-y-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <Button
          onClick={onBegin}
          size="lg"
          className="h-14 w-full rounded-full bg-primary text-primary-foreground glow-primary"
        >
          {beginCta}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          {skipCta}
        </Button>
        <p className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {discretion}
        </p>
      </footer>
    </motion.div>
  );
}

function QuestionPhase({
  index,
  total,
  questionId,
  text,
  value,
  onChange,
  scaleLabels,
  progressLabel,
  nextCta,
  isLast,
  backAria,
  onBack,
  onNext,
  skipCta,
  onSkip,
  discretion,
}: {
  index: number;
  total: number;
  questionId: BigFiveQuestionId;
  text: string;
  value: BigFiveAnswerValue;
  onChange: (next: number[]) => void;
  scaleLabels: readonly [string, string, string, string, string];
  progressLabel: string;
  nextCta: string;
  isLast: boolean;
  backAria: string;
  onBack: () => void;
  onNext: () => void;
  skipCta: string;
  onSkip: () => void;
  discretion: string;
}) {
  const progressPct = ((index + 1) / total) * 100;

  return (
    <motion.div
      key={questionId}
      className="relative z-10 flex flex-1 flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <header className="flex items-center gap-3 pt-5">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          aria-label={backAria}
          className="rounded-full"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="mb-1 text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            {progressLabel}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
        <div className="w-10" />
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-8 py-6 text-center">
        <p className="font-serif text-2xl leading-snug text-foreground sm:text-3xl">
          {text}
        </p>

        <div className="w-full space-y-4 px-1">
          <Slider
            value={[value]}
            onValueChange={onChange}
            min={1}
            max={5}
            step={1}
            size="lg"
            aria-label={text}
            className="py-2"
          />
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="max-w-[30%] text-left">{scaleLabels[0]}</span>
            <span className="max-w-[30%] text-right">{scaleLabels[4]}</span>
          </div>
          <p className="text-xs text-foreground/80">{scaleLabels[value - 1]}</p>
        </div>
      </main>

      <footer className="mx-auto w-full max-w-md space-y-3 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <Button
          onClick={onNext}
          size="lg"
          className={cn(
            'h-14 w-full rounded-full',
            'bg-primary text-primary-foreground glow-primary',
          )}
        >
          {nextCta}
          {!isLast && <ChevronRight className="ml-1 h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          className="w-full text-muted-foreground hover:text-foreground"
        >
          {skipCta}
        </Button>
        <p className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {discretion}
        </p>
      </footer>
    </motion.div>
  );
}

function SubmittingPhase({
  title,
  italic,
  body,
  locale: _locale,
}: {
  title: string;
  italic: string;
  body: string;
  locale: string;
}) {
  return (
    <motion.div
      className="relative z-10 flex flex-1 flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-5 py-6 text-center">
        <motion.div
          className="h-12 w-12 rounded-full border-2 border-primary/40 border-t-primary"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          aria-hidden
        />
        <h1 className="font-serif text-3xl leading-tight tracking-tight text-foreground sm:text-4xl">
          {title}
        </h1>
        <p className="font-serif italic text-base leading-relaxed text-muted-foreground sm:text-lg">
          {italic}
        </p>
        <p className="max-w-md text-balance text-sm leading-relaxed text-foreground/85">
          {body}
        </p>
      </main>
    </motion.div>
  );
}

function ThanksPhase({
  title,
  italic,
  body,
  ctaToCoach,
  onContinue,
  discretion,
}: {
  title: string;
  italic: string;
  body: string;
  ctaToCoach: string;
  onContinue: () => void;
  discretion: string;
}) {
  return (
    <motion.div
      className="relative z-10 flex flex-1 flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-5 py-6 text-center">
        <GlowLogo size={120} />
        <h1 className="font-serif text-4xl leading-tight tracking-tight text-foreground sm:text-5xl">
          {title}
        </h1>
        <p className="font-serif italic text-lg leading-relaxed text-muted-foreground sm:text-xl">
          {italic}
        </p>
        <p className="max-w-md text-balance text-sm leading-relaxed text-foreground/85 sm:text-base">
          {body}
        </p>
      </main>

      <footer className="mx-auto w-full max-w-md space-y-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <Button
          onClick={onContinue}
          size="lg"
          className="h-14 w-full rounded-full bg-primary text-primary-foreground glow-primary"
        >
          {ctaToCoach}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
        <p className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {discretion}
        </p>
      </footer>
    </motion.div>
  );
}
